import React, { useState, useEffect, useContext, DragEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { deleteReservationCustomExportSetting, fetchReservationCustomExportSetting, postReservationCustomExport } from '@/apis/aipass'
import { mainColumnStyle, wholeContainerStyle } from '@/constants/layout'
import { SideMenu } from '@/components/organisms/side-menu'
import { Header } from '@/components/organisms/header'
import { Button, ButtonType } from '@/components/atoms/button'
import { Hint } from '@/components/atoms/hint'
import { Select } from '@/components/atoms/select'
import { Select as AntSelect } from 'antd'
import uuid from 'react-uuid'
import { InputField } from '@/components/molecules/input-field'
import { draggableIconStyle, useDraggableDom } from '@/components/molecules/settings/use-draggable-dom'
import { useErrorHandler } from '@/hooks/use-error-handler'
import * as CustomExportModel from '@/models/reservation/custom-export'
import { useHistory, useLocation } from 'react-router-dom'
import { LoaderContextCreator } from '@/contexts/loader'
import { AccountContext } from '@/contexts/account'
import { ensureCsvExportPermissionOrAlert } from '@/libs/hotel-pms-permission'
import { DropDownMene } from '@/components/molecules/drop-down-menu'
import { CustomExportFormatEditModal } from '@/components/organisms/accommodation-management/custom-export-format-edit-modal'

export const CustomExport: React.FC = () => {
  const { errorHandler } = useErrorHandler()
  const { t } = useTranslation()
  const history = useHistory()
  const { state } = useLocation()
  const { setIsLoading } = useContext(LoaderContextCreator())
  const { permissions, permissionsLoaded, permissionsError } = useContext(AccountContext)

  const [isDisabledSave, setIsDisabledSave] = useState(false)
  const [formatName, setFormatName] = useState<string>('')
  const [selectedFormatId, setSelectedFormatId] = useState<string>('')
  const [storedFormats, setStoredFormats] = useState<CustomExportModel.Setting[]>([])
  const [allOutputItems, setAllOutputItems] = useState<CustomExportModel.Column[]>([])
  const [outputItems, setOutputItems] = useState<CustomExportModel.Column[]>([])
  const [nonOutputItems, setNonOutputItems] = useState<CustomExportModel.Column[]>([])
  const [isShowEditDialog, setIsShowEditDialog] = useState(false)

  const { dragStart, dragOver, dragDrop } = useDraggableDom<CustomExportModel.Column>({
    list: [...nonOutputItems, ...outputItems],
    onChange: () => {},
    direction: 'vertical',
    key: 'value',
  })

  const resetItemForm = (id: string, settings: CustomExportModel.Setting[]) => {
    const setting = settings.find(format => format.id === id)
    if (!setting) {
      return
    }
    setFormatName(setting.id !== '' ? setting.title : '')
    setOutputItems(setting.columns)
  }

  const loadExportSetting = async () => {
    try {
      const res = await fetchReservationCustomExportSetting()
      setAllOutputItems(res.allColumns)
      setStoredFormats(res.storedColumns)
    } catch (e) {
      errorHandler(e)
    }
  }

  const moveDraggableList = (event: DragEvent<HTMLDivElement>) => {
    event.stopPropagation()
    const res = dragDrop(event, true)
    if (!res) {
      return
    }

    const filteredDragList = outputItems.filter(col => col.value !== res.from)
    const isFromOutput = outputItems.some(col => col.value === res.from)

    if (res.to === 'non-output-item-block') {
      if (isFromOutput) {
        setOutputItems(filteredDragList)
      }
    } else {
      const fromCol = (isFromOutput ? outputItems : nonOutputItems).find(col => col.value === res.from)
      const toIndex = res.to === 'output-item-block' ? filteredDragList.length - 1 : filteredDragList.findIndex(col => col.value === res.to)
      filteredDragList.splice(res.isDropToAfter ? toIndex + 1 : toIndex, 0, fromCol!)
      setOutputItems(filteredDragList)
    }
  }

  const doExport = async (doSave: boolean) => {
    if (
      !ensureCsvExportPermissionOrAlert(permissions, t, 'customer_csv_export', {
        permissionsLoaded,
        permissionsError,
      })
    ) {
      return
    }
    try {
      setIsLoading(true)
      await postReservationCustomExport({
        setting: {
          doSave,
          id: selectedFormatId,
          title: formatName,
          columns: outputItems.map(col => col.value),
        },
        search: state,
      })
      history.push('/csv-download-history/list')
    } catch (e) {
      errorHandler(e)
    } finally {
      setIsLoading(false)
    }
  }

  const getTextColor = (col: CustomExportModel.Column) => {
    switch (col.category) {
      case CustomExportModel.Category.Reservation:
        return '#F2A40B'
      case CustomExportModel.Category.GuestRoom:
        return '#5AC931'
      case CustomExportModel.Category.Sales:
        return '#3E85CC'
      case CustomExportModel.Category.Checkin:
        return '#BA79E3'
      default:
        return '#676767'
    }
  }

  const confirmAndDeleteSetting = async () => {
    if (!window.confirm(`「${formatName}」を削除します。この操作は元に戻せません。`)) {
      return
    }
    try {
      setIsLoading(true)
      await deleteReservationCustomExportSetting(selectedFormatId)
      await loadExportSetting()
      setSelectedFormatId('')
    } catch (e) {
      errorHandler(e)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    const outputItemValues = outputItems.map(col => col.value)
    setNonOutputItems(allOutputItems.filter(col => !outputItemValues.includes(col.value)))
  }, [outputItems])

  useEffect(() => {
    setIsDisabledSave(selectedFormatId === '' && !formatName?.length)
  }, [selectedFormatId, formatName])

  useEffect(() => {
    resetItemForm(selectedFormatId, storedFormats)
  }, [selectedFormatId, storedFormats])

  useEffect(() => {
    if (!state) {
      history.replace({ pathname: '/dashboard' })
      return
    }
    if (!permissionsLoaded) {
      return
    }

    const init = async () => {
      if (
        !ensureCsvExportPermissionOrAlert(permissions, t, 'customer_csv_export', {
          permissionsLoaded,
          permissionsError,
        })
      ) {
        history.goBack()
        return
      }
      try {
        await loadExportSetting()
      } catch (e) {
        errorHandler(e)
        history.goBack()
      }
    }

    init()
  }, [state, permissionsLoaded, permissionsError, permissions])

  return (
    <div css={wholeContainerStyle}>
      <SideMenu />
      <div css={mainColumnStyle}>
        <Header title={t('Accommodation management')} />
        <div css={mainContainerStyle}>
          <div className="export-form">
            <div className="format-select-block">
              <div className="input-block">
                <div className="__label">{t('Format')}</div>
                <div style={{ position: 'relative' }}>
                  <Select
                    value={selectedFormatId}
                    onChange={e => {
                      setSelectedFormatId((e as unknown) as string)
                    }}
                    customStyle={css({ paddingBottom: 0 })}
                  >
                    {storedFormats.map(format => (
                      <AntSelect.Option key={format.id || uuid()} value={format.id}>
                        {format.title}
                      </AntSelect.Option>
                    ))}
                  </Select>
                  {selectedFormatId !== '' && (
                    <DropDownMene
                      className="format-menu-block"
                      items={[
                        {
                          onClick: () => setIsShowEditDialog(true),
                          element: (
                            <>
                              <img
                                src={require('@/static/images/edit_no_margin.svg')}
                                style={{
                                  width: 14,
                                  marginRight: 12,
                                }}
                              ></img>
                              <p>{t('Edit')}</p>
                            </>
                          ),
                        },
                        {
                          onClick: confirmAndDeleteSetting,
                          element: (
                            <>
                              <img
                                src={require('@/static/images/icon_delete_warning.svg')}
                                style={{
                                  width: 20, // 画像（左右3px）分加算
                                  marginRight: 8,
                                  marginLeft: -3,
                                }}
                              ></img>
                              <p style={{ color: '#f44d10' }}>{t('Delete')}</p>
                            </>
                          ),
                        },
                      ]}
                      direction="right"
                    >
                      <div css={editIconStyle}></div>
                    </DropDownMene>
                  )}
                </div>
              </div>
              {selectedFormatId === '' && (
                <div className="input-block" style={{ paddingTop: 24 }}>
                  <div className="__label">
                    {t('Format name')}
                    <span>※</span>
                  </div>
                  <InputField required={true} value={formatName} onChange={e => setFormatName(e.target.value)} />
                </div>
              )}
            </div>

            <div style={{ borderLeft: '1px solid rgb(242, 242, 242)', margin: '32px 0' }}></div>

            <div className="format-item-block __left">
              <div className="__label">
                <div>{t('Non output items')}</div>
                <Hint customCss={helpPopupStyle}>
                  <p>各項目をドラッグ&ドロップすることで、項目をカスタマイズできます。</p>
                  <img src={require('@/static/images/export-csv-operate.png')}></img>
                </Hint>
              </div>
              <div className="draggable-block" id="non-output-item-block" onDragOver={dragOver} onDrop={moveDraggableList}>
                {nonOutputItems.map((item, i) => (
                  <div
                    id={item.value}
                    key={item.value}
                    style={{ position: 'relative' }}
                    draggable
                    css={[draggableIconStyle, css({ '&:hover:before': { left: 8, top: 4 } })]}
                    onDragStart={dragStart}
                  >
                    <div className="column-item" style={{ color: getTextColor(item) }}>
                      <span>{i + 1}</span>
                      {item.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="swap-icon-block">
              <div>
                <img src={require('@/static/images/icon_arrow_right.svg')} />
              </div>
              <div>
                <img src={require('@/static/images/icon_arrow_left.svg')} />
              </div>
            </div>

            <div className="format-item-block __right">
              <div className="__label">
                <div>{t('Output items')}</div>
                <Hint customCss={helpPopupStyle}>
                  <p>青色の項目（利用明細）を出力項目に追加すると、1部屋に紐づく明細が2つ以上の場合はCSV内に空欄ができます。</p>
                  <img src={require('@/static/images/export-csv-example.png')}></img>
                </Hint>
              </div>
              <div className="draggable-block">
                {outputItems.map((item, i) => (
                  <div
                    id={item.value}
                    key={item.value}
                    style={{ position: 'relative' }}
                    draggable
                    css={[draggableIconStyle, css({ '&:hover:before': { left: 8, top: 4 } })]}
                    onDragStart={dragStart}
                    onDragOver={dragOver}
                    onDrop={moveDraggableList}
                  >
                    <div className="column-item" style={{ color: getTextColor(item) }}>
                      <span>{i + 1}</span>
                      {item.label}
                    </div>
                  </div>
                ))}

                <div
                  id="output-item-block"
                  style={{ position: 'relative', height: '100%' }}
                  onDragOver={dragOver}
                  onDrop={moveDraggableList}
                ></div>
              </div>
            </div>
          </div>
        </div>

        <div css={footerStyle}>
          <div>
            <Button buttonType={ButtonType.Secondary} width={110} height={38} onClick={() => history.goBack()}>
              {t('Cancel')}
            </Button>
          </div>
          <div className="button-group">
            <Button buttonType={ButtonType.Secondary} width={142} height={38} onClick={() => doExport(false)}>
              {t('Export without saving')}
            </Button>
            <Button
              buttonType={isDisabledSave ? ButtonType.PrimaryDisabled : ButtonType.Primary}
              width={124}
              height={38}
              isDisabled={isDisabledSave}
              onClick={() => doExport(true)}
            >
              {t('Export with save')}
            </Button>
          </div>
        </div>
      </div>
      {isShowEditDialog && (
        <CustomExportFormatEditModal
          setting={storedFormats.find(format => format.id === selectedFormatId)!}
          onCancel={() => setIsShowEditDialog(false)}
          onUpdate={() => {
            loadExportSetting()
            setIsShowEditDialog(false)
          }}
        />
      )}
    </div>
  )
}

const mainContainerStyle = css({
  display: 'flex',
  alignItems: 'start',
  // footer:60, header:55
  height: 'calc(100vh - 60px - 55px)',
  padding: 24,
  '.export-form': {
    background: '#FFFFFF 0% 0% no-repeat padding-box',
    boxShadow: '0px 0px 6px #0000001A',
    borderRadius: 5,
    height: '100%',
    width: '100%',
    display: 'flex',
    '.format-select-block': {
      padding: 32,
      '.input-block': { maxWidth: 277, minWidth: 227 },
    },
    '.format-item-block': {
      padding: 32,
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
      width: 'min-content',
      '&.__left': { paddingRight: 0 },
      '&.__right': { paddingLeft: 0 },
      '.draggable-block': {
        padding: 16,
        border: '1px solid #CCCCCC',
        borderRadius: 12,
        flexGrow: 1,
        overflowY: 'scroll',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        '.column-item': {
          paddingLeft: 52,
          border: '1px solid #CCCCCC',
          borderRadius: 5,
          height: 38,
          display: 'flex',
          alignItems: 'center',
          fontSize: 14,
          fontWeight: 'bold',
          '>span': { paddingRight: 16 },
        },
        '.draggable-line': {
          '&.-top': { borderTop: 'solid 2px #f2a40b', top: -5 },
          '&.-bottom': { borderBottom: 'solid 2px #f2a40b', bottom: -5 },
          position: 'absolute',
          width: '100%',
        },
      },
    },
    '.swap-icon-block': {
      width: 64,
      alignSelf: 'center',
      justifyItems: 'center',
    },
    '.format-menu-block': {
      position: 'absolute',
      top: 1,
      right: 30,
    },
    '.__label': {
      fontWeight: 'bold',
      fontSize: 12,
      letterSpacing: 0.6,
      color: '#676767',
      paddingBottom: 12,
      display: 'flex',
      position: 'relative',
      '> span': {
        paddingLeft: 8,
        fontSize: 8,
        letterSpacing: 0.4,
      },
    },
  },
})

const footerStyle = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  background: '#FFFFFF 0% 0% no-repeat padding-box',
  boxShadow: '0px 0px 6px #0000001A',
  height: 60,
  padding: '0 32px 0 24px',
  '.button-group': {
    display: 'flex',
    gap: 16,
  },
})

const helpPopupStyle = css({
  paddingLeft: 8,
  position: 'static',
  '.help-popup': {
    width: 386,
    left: 0,
    padding: 16,
    '> p': {
      letterSpacing: 1.2,
      color: '#676767',
      fontWeight: 'bold',
      fontSize: 12,
      lineHeight: 1.5,
    },
    '> img': {
      paddingTop: 16,
      width: 354,
    },
  },
})

const editIconStyle = css({
  display: 'block',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center',
  borderRadius: '50px',
  width: '30px',
  height: '30px',
  backgroundImage: `url(${require('@/static/images/icon_other.svg')})`,
  cursor: 'pointer',
  ':hover': { backgroundColor: '#f2f2f2' },
})
